import { useEffect } from 'react';

interface DocumentHeadOptions {
  title?: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  ogUrl?: string;
  ogLocale?: string;
  ogSiteName?: string;
  canonical?: string;
  noindex?: boolean;
  twitterCard?: 'summary' | 'summary_large_image';
}

/**
 * Hook to manage document head metadata for SEO
 * Updates title, meta description, Open Graph tags, etc.
 */
export function useDocumentHead(options: DocumentHeadOptions) {
  useEffect(() => {
    const {
      title,
      description,
      ogTitle,
      ogDescription,
      ogImage,
      ogType = 'website',
      ogUrl,
      ogLocale = 'fr_FR',
      ogSiteName = 'Luminjo',
      canonical,
      noindex = false,
      twitterCard = 'summary_large_image',
    } = options;

    // Store original values to restore on unmount
    const originalTitle = document.title;

    // Update title
    if (title) {
      document.title = title;
    }

    // Helper to set or update meta tags
    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Helper to remove meta tags
    const removeMetaTag = (name: string, property = false) => {
      const attr = property ? 'property' : 'name';
      const meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (meta) {
        meta.remove();
      }
    };

    // Set meta description
    if (description) {
      setMetaTag('description', description);
    }

    // Default OG image fallback
    const defaultOgImage = `${window.location.origin}/og-image.png`;
    const finalOgImage = ogImage || defaultOgImage;

    // Set Open Graph tags
    if (ogTitle || title) {
      setMetaTag('og:title', ogTitle || title || '', true);
    }
    if (ogDescription || description) {
      setMetaTag('og:description', ogDescription || description || '', true);
    }
    setMetaTag('og:image', finalOgImage, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', ogUrl || canonical || window.location.href, true);
    setMetaTag('og:locale', ogLocale, true);
    setMetaTag('og:site_name', ogSiteName, true);

    // Set Twitter Card tags
    setMetaTag('twitter:card', twitterCard);
    if (ogTitle || title) {
      setMetaTag('twitter:title', ogTitle || title || '');
    }
    if (ogDescription || description) {
      setMetaTag('twitter:description', ogDescription || description || '');
    }
    setMetaTag('twitter:image', finalOgImage);

    // Set canonical URL
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonical;
    }

    // Set robots meta
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    }

    // Cleanup on unmount
    return () => {
      document.title = originalTitle;
      // Note: We don't remove meta tags on unmount to avoid flickering
      // They will be updated by the next page
    };
  }, [
    options.title,
    options.description,
    options.ogTitle,
    options.ogDescription,
    options.ogImage,
    options.ogType,
    options.ogUrl,
    options.ogLocale,
    options.ogSiteName,
    options.canonical,
    options.noindex,
    options.twitterCard,
  ]);
}
